import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  edit_profile,
  get_user_profile,
} from "../../../../services/authentication";
import { asyncStatus } from "../../../../utils/asyncStatus";
import { setUserProfileUpdateStatus } from "../../../../store/slices/user_auth_slice";
import { imgUrl, getImageUrl } from "../../../../utils/imageUrl";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { themeOrange } from "../../../../utils/colorTheme";
import { BiExport } from "react-icons/bi";
import CustomModal from "../../../../component/common/CustomModal/CustomModal";
import { RxCross2 } from "react-icons/rx";
import { MdLibraryAdd, MdOutlineEdit } from "react-icons/md";
import Input from "../../../../component/common/Input";
import ReactStars from "react-rating-stars-component";
import profile from "../../../../assets/profile.webp";
import CustomPhoneInput from "../../../../component/common/CustomPhoneInput/CustomPhoneInput";

const VendorProfileTab = () => {
  const [isDisable, setIsDisable] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [data, setData] = useState({});
  const [inputErrors, setInputErrors] = useState({
    first_name_err: "",
    last_name_err: "",
  });

  const {
    get_user_profile_status,
    get_user_profile_data,
    edit_profile_status,
    vendor_rating,
    user,
  } = useSelector((state) => state.userAuth);

  const dispatch = useDispatch();

  const validateName = (name, field) => {
    const fieldName =
      field.split("_").join(" ").charAt(0).toUpperCase() +
      field.split("_")[0].slice(1) +
      " " +
      field.split("_")[1].charAt(0).toUpperCase() +
      field.split("_")[1].slice(1);
    let error = "";
    if (!name.trim()) {
      error = `${fieldName} cannot be empty.`;
    } else if (/\d/.test(name.charAt(0))) {
      error = `${fieldName} cannot start with a number.`;
    } else if (/^[a-z]/.test(name.charAt(0))) {
      error = `${fieldName} must start with a capital letter.`;
    }
    return error;
  };

  const _handleFieldsChange = (key, value) => {
    let error = "";
    if (key === "first_name" || key === "last_name") {
      error = validateName(value, key);
      setInputErrors((prevErrors) => ({
        ...prevErrors,
        [`${key}_err`]: error,
      }));
    }
    if (!error) {
      setData({ ...data, [key]: value });
    }
  };

  useEffect(() => {
    dispatch(get_user_profile());
  }, []);

  const profileDataLoader = get_user_profile_status === asyncStatus.LOADING;
  const profileUpdateLoader = edit_profile_status === asyncStatus.LOADING;

  const userProfileData = [
    {
      id: "first_name",
      label: "First Name",
      value: "",
      disable: !isDisable,
      type: "text",
      placeholder: "First Name",
      defaultVal:
        get_user_profile_data?.first_name?.charAt(0).toUpperCase() +
        get_user_profile_data?.first_name?.slice(1),
      onchange: (e) => _handleFieldsChange(e.target.id, e.target.value),
      error: inputErrors.first_name_err,
    },
    {
      id: "last_name",
      label: "Last Name",
      value: "",
      disable: !isDisable,
      type: "text",
      placeholder: "Last Name",
      defaultVal:
        get_user_profile_data?.last_name?.charAt(0).toUpperCase() +
        get_user_profile_data?.last_name?.slice(1),
      onchange: (e) => _handleFieldsChange(e.target.id, e.target.value),
      error: inputErrors.last_name_err,
    },
    {
      id: "email",
      label: "Email ID",
      value: "",
      disable: !isDisable,
      type: "email",
      placeholder: "Email Address",
      defaultVal: get_user_profile_data?.email,
      onchange: (e) => _handleFieldsChange(e.target.id, e.target.value),
    },
    {
      id: "phone",
      label: "Phone Number",
      value: data.phone,
      disable: !isDisable,
      type: "text",
      placeholder: "Phone Number",
      defaultVal: get_user_profile_data?.phone,
      onchange: (e) => _handleFieldsChange("phone", e),
    },
    {
      id: "Status",
      label: "Status",
      value: +get_user_profile_data?.status === 1 ? "Approved" : "Pending",
      disable: true,
      type: "text",
      placeholder: "Status",
      defaultVal: +get_user_profile_data?.status === 1 ? "Approved" : "Pending",
      onchange: () => {},
    },
  ];

  const _handleModal = () => {
    setIsOpenModal(false);
  };

  const _handleUploadClick = () => {
    document.getElementById("upload-input").click();
  };

  const _handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handleEdit = () => {
    dispatch(edit_profile(data));
  };

  useEffect(() => {
    if (edit_profile_status === asyncStatus.SUCCEEDED) {
      setIsDisable(false);
      setIsOpenModal(false);
      dispatch(setUserProfileUpdateStatus());
    }
  }, [edit_profile_status]);

  const _handleUploadImage = () => {
    setData({ ...data, image: selectedImage });
    setIsOpenModal(!isOpenModal);
    setSelectedImage(null);
  };

  const displayedImage = data.image
    ? URL.createObjectURL(data.image)
    : get_user_profile_data?.avatar || `${imgUrl}${profile}`;

  const isButtonDisabled = !!inputErrors.first_name_err || !!inputErrors.last_name_err;

  return (
    <Stack spacing={3}>
      <hr color="#D1D1D1" style={{ margin: 0 }} />

      {profileDataLoader ? (
        <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }}>
          <CircularProgress size={30} sx={{ color: themeOrange }} />
        </Stack>
      ) : !get_user_profile_data && !get_user_profile_data?.id ? (
        <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }}>
          <Typography sx={{ color: "#aaa", fontWeight: "500" }}>No profile found</Typography>
        </Stack>
      ) : (
        <Stack spacing={3}>
          {/* Profile header card */}
          <Box
            sx={{
              borderRadius: "14px",
              border: "1.5px solid #ebebeb",
              backgroundColor: "white",
              p: 2.5,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
              gap={2}
            >
              {/* Avatar + name + rating */}
              <Stack direction="row" alignItems="center" gap={2.5}>
                {/* Avatar with upload overlay */}
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                  <Avatar
                    src={displayedImage || ""}
                    sx={{
                      width: 90,
                      height: 90,
                      borderRadius: "12px",
                      border: "2px solid #f0f0f0",
                    }}
                  />
                  {isDisable && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -6,
                        right: -10,
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        backgroundColor: "#353535",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        zIndex: 10,
                      }}
                      onClick={() => setIsOpenModal(true)}
                    >
                      <BiExport color="#EC9430" style={{ fontSize: "14px" }} />
                    </Box>
                  )}
                </Box>

                {/* Name + rating */}
                <Stack spacing={0.5}>
                  <Typography
                    sx={{
                      fontWeight: "700",
                      fontSize: "18px",
                      color: "#1a1a1a",
                      textTransform: "capitalize",
                      lineHeight: 1.3,
                    }}
                  >
                    {`${get_user_profile_data?.first_name || ""} ${
                      get_user_profile_data?.last_name || ""
                    }`}
                  </Typography>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography sx={{ fontSize: "13px", color: "#888", fontWeight: "600" }}>
                      {vendor_rating || 0}/5
                    </Typography>
                    <ReactStars
                      edit={false}
                      count={5}
                      value={vendor_rating || 0}
                      size={18}
                      isHalf={true}
                      emptyIcon={<i className="far fa-star" />}
                      halfIcon={<i className="fa fa-star-half-alt" />}
                      fullIcon={<i className="fa fa-star" />}
                      activeColor="#ffd700"
                    />
                  </Stack>
                </Stack>
              </Stack>

              {/* Edit / Cancel button */}
              <Button
                variant={isDisable ? "outlined" : "contained"}
                startIcon={isDisable ? null : <MdOutlineEdit size={16} />}
                onClick={() => setIsDisable(!isDisable)}
                sx={
                  isDisable
                    ? {
                        borderColor: "#e0e0e0",
                        color: "#555",
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: "600",
                        fontSize: "14px",
                        px: 2.5,
                        py: 1,
                        flexShrink: 0,
                        "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#ccc" },
                      }
                    : {
                        background:
                          "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)",
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: "700",
                        fontSize: "14px",
                        px: 2.5,
                        py: 1,
                        flexShrink: 0,
                        boxShadow: "none",
                        "&:hover": {
                          background:
                            "linear-gradient(180deg, #e04d1a 0%, #b3370d 100%)",
                          boxShadow: "none",
                        },
                      }
                }
              >
                {isDisable ? "Cancel" : "Edit Profile"}
              </Button>
            </Stack>
          </Box>

          {/* Profile fields */}
          <Grid container spacing={2}>
            {userProfileData.map(
              ({ id, label, value, disable, type, placeholder, defaultVal, onchange, error }) => {
                const isPhone = id === "phone";

                return (
                  <Grid item xl={6} lg={6} md={6} sm={12} xs={12} key={id}>
                    {isPhone ? (
                      disable ? (
                        /* Phone — view mode */
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: "12px",
                            border: "1.5px solid #ebebeb",
                            backgroundColor: "#fafafa",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: "700",
                              color: "#aaa",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              mb: 0.5,
                            }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            sx={{ fontSize: "15px", fontWeight: "600", color: "#1a1a1a" }}
                          >
                            {defaultVal || (
                              <span style={{ color: "#ccc", fontStyle: "italic", fontWeight: 400 }}>
                                Not set
                              </span>
                            )}
                          </Typography>
                        </Box>
                      ) : (
                        /* Phone — edit mode */
                        <CustomPhoneInput
                          value={value}
                          onChange={onchange}
                          error={error}
                          defaultValue={defaultVal}
                          disabled={disable}
                        />
                      )
                    ) : disable ? (
                      /* Text fields — view mode */
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          border: "1.5px solid #ebebeb",
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: "700",
                            color: "#aaa",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            mb: 0.5,
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "15px",
                            fontWeight: "600",
                            color:
                              id === "Status"
                                ? defaultVal === "Approved"
                                  ? "#2e7d32"
                                  : "#e65100"
                                : "#1a1a1a",
                            textTransform: id === "Status" ? "none" : "capitalize",
                            wordBreak: "break-all",
                          }}
                        >
                          {defaultVal || (
                            <span style={{ color: "#ccc", fontStyle: "italic", fontWeight: 400 }}>
                              Not set
                            </span>
                          )}
                        </Typography>
                      </Box>
                    ) : (
                      /* Text fields — edit mode */
                      <Stack spacing={0.5}>
                        <Input
                          id={id}
                          type={type}
                          onChange={onchange}
                          placeholder={placeholder}
                          label={label}
                          disabled={disable}
                          defaultValue={defaultVal}
                        />
                        {error && (
                          <Typography sx={{ color: "#ef5350", fontSize: "12px", fontWeight: "500" }}>
                            {error}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Grid>
                );
              }
            )}
          </Grid>

          {/* Save button (edit mode only) */}
          {isDisable && (
            <Button
              variant="contained"
              fullWidth
              onClick={handleEdit}
              disabled={isButtonDisabled || profileUpdateLoader}
              sx={{
                background: "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: "700",
                fontSize: "15px",
                py: 1.5,
                boxShadow: "none",
                "&:hover": {
                  background: "linear-gradient(180deg, #e04d1a 0%, #b3370d 100%)",
                  boxShadow: "none",
                },
                "&.Mui-disabled": { opacity: 0.6 },
              }}
            >
              {profileUpdateLoader ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : (
                "Save Changes"
              )}
            </Button>
          )}

          {/* Services section */}
          {user?.services?.length > 0 && (
            <Stack spacing={2}>
              <Typography
                sx={{ fontSize: "22px", fontWeight: "500", color: "#333", fontFamily: "Raleway" }}
              >
                My{" "}
                <Typography
                  component="span"
                  sx={{ fontSize: "22px", fontWeight: "700", color: "#333", fontFamily: "Raleway" }}
                >
                  Services
                </Typography>
              </Typography>

              <Grid container spacing={2}>
                {user.services.map(({ service }, i) => (
                  <Grid key={i} item xl={4} lg={4} md={4} sm={6} xs={12}>
                    <Box
                      sx={{
                        borderRadius: "12px",
                        border: "1.5px solid #ebebeb",
                        backgroundColor: "white",
                        overflow: "hidden",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        display: "flex",
                        alignItems: "center",
                        transition: "box-shadow 0.2s",
                        "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.1)" },
                      }}
                    >
                      <Box sx={{ width: 90, height: 80, flexShrink: 0 }}>
                        <img
                          src={getImageUrl(service?.mobile_image)}
                          alt={service?.name || ""}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          px: 2,
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#1a1a1a",
                          lineHeight: 1.4,
                        }}
                      >
                        {service?.name || ""}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          )}
        </Stack>
      )}

      {/* Image upload modal */}
      <CustomModal isOpen={isOpenModal} setIsOpen={_handleModal}>
        <IconButton
          sx={{ position: "absolute", top: "12px", right: "12px" }}
          onClick={() => {
            setIsOpenModal(false);
            setSelectedImage(null);
          }}
        >
          <RxCross2 style={{ fontSize: "18px", color: "#666" }} />
        </IconButton>

        <Stack
          spacing={2.5}
          sx={{
            backgroundColor: "white",
            p: { xs: 3, sm: 4 },
            borderRadius: "16px",
            width: { xl: "28vw", lg: "35vw", md: "50vw", sm: "70vw", xs: "85vw" },
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: "700", fontSize: "17px", color: "#1a1a1a" }}>
            Update Profile Photo
          </Typography>

          {/* Preview */}
          {selectedImage && (
            <Box
              sx={{
                width: 130,
                height: 130,
                borderRadius: "12px",
                overflow: "hidden",
                border: "2px solid #f0f0f0",
              }}
            >
              <img
                src={URL.createObjectURL(selectedImage)}
                alt="preview"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          )}

          {/* Upload area */}
          <Box
            onClick={_handleUploadClick}
            sx={{
              width: "100%",
              p: 3,
              borderRadius: "12px",
              border: "2px dashed #e0e0e0",
              backgroundColor: "#fafafa",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              transition: "border-color 0.2s, background-color 0.2s",
              "&:hover": {
                borderColor: themeOrange,
                backgroundColor: `${themeOrange}05`,
              },
            }}
          >
            <input
              id="upload-input"
              type="file"
              multiple
              accept="image/*"
              onChange={_handleFileChange}
              style={{ display: "none" }}
            />
            <MdLibraryAdd size={36} color={themeOrange} />
            <Typography sx={{ fontSize: "14px", color: "#888", fontWeight: "500" }}>
              Click to select a photo
            </Typography>
          </Box>

          {/* Confirm button */}
          <Button
            variant="contained"
            fullWidth
            onClick={_handleUploadImage}
            disabled={!selectedImage}
            sx={{
              background: "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: "700",
              fontSize: "14px",
              py: 1.25,
              boxShadow: "none",
              "&:hover": {
                background: "linear-gradient(180deg, #e04d1a 0%, #b3370d 100%)",
                boxShadow: "none",
              },
              "&.Mui-disabled": { opacity: 0.5 },
            }}
          >
            Save Photo
          </Button>
        </Stack>
      </CustomModal>
    </Stack>
  );
};

export default VendorProfileTab;
